import { Option, Schema } from 'effect';

import type { CurrencyT } from '../../../../config/currency';

import type { CartItemEventData } from '../../../bind/cartMutationItem';
import { GtmAddToCartEventSchema } from '@vs-shop/schemas/gtm/add-to-cart';
import { GtmRemoveFromCartEventSchema } from '@vs-shop/schemas/gtm/remove-from-cart';

export const createAddToCartPayloadFromCartItem = (
  cartItem: CartItemEventData,
  amount: number,
  currency: CurrencyT
) => {
  const unitPrice = cartItem.unitPriceWithoutTax / 100;
  const unitPriceWithTax = cartItem.unitPriceBeforeDiscount;

  return Schema.decodeEffect(GtmAddToCartEventSchema)({
    event: 'track.add_to_cart',
    ecommerce: {
      currency: currency,
      value: unitPrice * amount,
      items: [
        {
          id: cartItem.variantCode,
          name: cartItem.name,
          price: unitPrice,
          price_with_tax: unitPriceWithTax / 100,
          quantity: amount,
          //todo brand and categories
        },
      ],
    },
  });
};

export type ProductForGTM = {
  priceWithoutTax: number;
  price: number;
  code: string;
  name: string;
  brand?: {
    name: string | null;
  } | null;
  mainTaxonCode?: string | null;
  category?: string | null;
};

export const createAddToCartPayloadFromProduct = (
  product: ProductForGTM,
  amount: number,
  currency: CurrencyT
) => {
  const productPriceAmount = product.priceWithoutTax / 100;
  const brand = Option.fromNullishOr(product.brand).pipe(
    Option.flatMap((productBrand) => Option.fromNullishOr(productBrand.name))
  );
  const categories = Option.fromNullishOr(product.mainTaxonCode).pipe(
    Option.filter((code) => code.length > 0),
    Option.map((code) => [code])
  );

  return Schema.decodeEffect(GtmAddToCartEventSchema)({
    event: 'track.add_to_cart',
    ecommerce: {
      currency: currency,
      value: productPriceAmount * amount,
      items: [
        {
          id: product.code,
          name: product.name,
          ...Option.match(brand, {
            onNone: () => ({}),
            onSome: (value) => ({ brand: value }),
          }),
          ...Option.match(categories, {
            onNone: () => ({}),
            onSome: (value) => ({ categories: value }),
          }),
          price: productPriceAmount,
          price_with_tax: product.price / 100,
          quantity: amount,
        },
      ],
    },
  });
};

export const createRemoveFromCartPayloadFromCartItem = (
  cartItem: CartItemEventData,
  amount: number,
  currency: CurrencyT
) => {
  const unitPrice = cartItem.unitPriceWithoutTax / 100;
  const unitPriceWithTax = cartItem.unitPriceBeforeDiscount;

  return Schema.decodeEffect(GtmRemoveFromCartEventSchema)({
    event: 'track.remove_from_cart',
    ecommerce: {
      currency: currency,
      value: unitPrice * amount,
      items: [
        {
          id: cartItem.variantCode,
          name: cartItem.name,
          price: unitPrice,
          price_with_tax: unitPriceWithTax / 100,
          quantity: amount,
          //todo brand and categories
        },
      ],
    },
  });
};
